/*
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
 * Distributed under the TruVideo proprietary license.
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const HIGHLIGHT_MS = 1000;

// Number went up (item moved down the list) vs. down (moved up the list).
const HIGHLIGHT_CLASSES = {
  increased: 'bg-warning text-warning-content scale-110 shadow-sm',
  decreased: 'bg-success text-success-content scale-110 shadow-sm',
};

/**
 * An item's 1-based position. When it changes (reorder, or an item above was
 * deleted) it's highlighted for a second, in one colour when the number went up
 * and another when it went down, so the move is easy to follow.
 */
const ChecklistItemNumber = ({ number, className = '' }) => {
  const [change, setChange] = useState(null);
  const previousRef = useRef(number);

  useEffect(() => {
    const previous = previousRef.current;
    if (previous === number) return undefined;
    previousRef.current = number;
    setChange(number > previous ? 'increased' : 'decreased');
    const timer = setTimeout(() => setChange(null), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [number]);

  return (
    <span
      className={`inline-flex items-center justify-center min-w-[1.75rem] h-5 px-1 rounded-md text-xs font-bold tabular-nums select-none transition-all duration-300 ${
        change ? HIGHLIGHT_CLASSES[change] : 'bg-transparent text-base-content/40'
      } ${className}`}
    >
      {number}.
    </span>
  );
};

ChecklistItemNumber.propTypes = {
  number: PropTypes.number.isRequired,
  className: PropTypes.string,
};

export default ChecklistItemNumber;
